import { createContext, useContext, useEffect, useState } from 'react'
import { NextPage } from 'next'
import { useRouter } from 'next/router'
import { Panel, Tabs } from '@bumaga/tabs'
import classnames from 'classnames'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBook, faCaretRight } from '@fortawesome/free-solid-svg-icons'
import { faApple, faSpotify } from '@fortawesome/free-brands-svg-icons'
import useInView from 'react-cool-inview'
import Tab from './Components/Tab'
import styles from './_songPage.module.scss'
import artists, { joinedArtistsStr } from '../../../utils/songs/artists'
import { t } from '../../../utils/i18n'
import {
  albumPath,
  songsPath,
  storeSongPath
} from '../../../utils/routeHelpers'
import {
  SONG_DETAILS_QUERY,
  SONG_VIDEO_QUERY,
  SONGS_LIST_QUERY,
  SONG_LYRICS_QUERY,
  SONG_CHORDS_QUERY
} from '../../../gql/queries'
import Layout from '../../../components/Layout'
import { useLazyQuery, useMutation } from '@apollo/client'
import {
  FAVORITE_SONG_TOGGLE_MUTATION,
  SAVE_SONG_SETTINGS_MUTATION
} from '../../../gql/mutations'
import { ModalsContext } from '../../../utils/modals'
import { Change, ChordItem } from '../../../utils/songs/songTabsCommon'
import { filterStateToQueryVariables, initalFiltersState } from '..'
import { useAuth } from '../../../utils/auth'
import PcoButton, { SongSection } from '../../../components/PcoButton'
import { withLogin } from '../../../utils/auth/withLogin'
import { DropdownOption } from '../../../components/Dropdown'
import dynamic from 'next/dynamic'
import { staticPath, staticProps } from '../../../utils/createApolloClient'
import {
  capoOptions,
  keyOfOptions
} from '../../../utils/songs/chordCharts.constants'
import LyricsTab from './Components/Lyrics'
import {
  Song,
  ChordSection,
  FavoriteSongToggleMutationPayload
} from '../../../__generated__/graphql'

const SongInstruments = dynamic(() => import('./Components/SongInstruments'))
const TabInfosTab = dynamic(() => import('./Components/TabInfos'))
const ChordChartsTab = dynamic(() => import('./Components/ChordCharts'))

const SongCreditsStoreLinks = ({ song }) => {
  if (!(song.hasKeyPatches || song.hasMultiTracks)) {
    return <></>
  }

  return (
    <div className={`${styles.block} ${styles.songStore}`}>
      <h3>{t('Store')}</h3>

      <ul>
        {song.hasMultiTracks ? (
          <li>
            <a href={storeSongPath(song)}>
              {t('MultiTracks')}
              <FontAwesomeIcon icon={faCaretRight} />
            </a>
          </li>
        ) : (
          <></>
        )}
        {song.hasKeyPatches ? (
          <li>
            <a href={storeSongPath(song) + '?tab=premium_patches'}>
              {t('Keys Patches')}
              <FontAwesomeIcon icon={faCaretRight} />
            </a>
          </li>
        ) : (
          <></>
        )}
      </ul>
    </div>
  )
}

const SingleSongActionButtons: React.FC<{
  song: Song
  favorite: boolean
  toggleFavorite: () => void
  addToSetlist: () => void
  songStructure?: Array<SongSection>
  songKey?: string
  capo?: string
  chordCharts: ChordItem[]
}> = ({
  song,
  favorite,
  toggleFavorite,
  addToSetlist,
  songStructure,
  songKey,
  capo,
  chordCharts
}) => {
  const { currentUser } = useAuth()
  const { openModal, closeModal } = useContext(ModalsContext)

  return (
    <div className={styles.actionButtons}>
      <div className={styles.actionButton}>
        <button
          className={classnames(
            'button',
            'is-wide',
            favorite ? 'is-purple' : 'is-transparent'
          )}
          onClick={() => {
            withLogin(toggleFavorite, currentUser, openModal, closeModal)
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="16"
            viewBox="0 0 18 16"
          >
            <g>
              <g>
                <path d="M16.297 1.337c-1.815-1.783-4.758-1.783-6.573 0l-.895.88-.896-.88c-1.814-1.782-4.757-1.782-6.572 0a4.506 4.506 0 0 0 0 6.454l.896.88 6.572 6.453L15.4 8.67l.896-.879a4.505 4.505 0 0 0 0-6.454z" />
              </g>
            </g>
          </svg>
          {favorite ? t('Favorited') : t('Add to favorites')}
        </button>
      </div>
      <div className={styles.actionButton}>
        <button
          className="button is-transparent is-wide"
          onClick={() => {
            withLogin(addToSetlist, currentUser, openModal, closeModal)
          }}
        >
          <span className={styles.plus} />
          {t('Add to setlist')}
        </button>
      </div>
      <div className={styles.actionButton}>
        <PcoButton
          inList={false}
          song={song}
          songStructure={songStructure}
          songKey={songKey}
          chordCharts={chordCharts}
          capo={capo}
        />
      </div>
      <div className={styles.actionButton}>
        {song.devotional && (
          <a href="#devotional">
            <button className="button is-transparent is-wide">
              <FontAwesomeIcon
                icon={faBook}
                className={styles.devotionalIcon}
              />
              Devotional
            </button>
          </a>
        )}
      </div>
    </div>
  )
}

const SongCredits: React.FC<{
  song: Song
  favorite: boolean
  toggleFavorite: () => void
  addToSetlist: () => void
  songStructure?: Array<SongSection>
  songKey?: string
  capo?: string
  chordCharts: ChordItem[]
  copyrights: string
}> = ({
  song,
  toggleFavorite,
  favorite,
  addToSetlist,
  songStructure,
  songKey,
  capo,
  chordCharts
}) => (
  <aside className="is-hidden-print is-hidden-mobile">
    <div className={styles.block}>
      <h3>{t('Song Credits')}</h3>
      <SingleSongActionButtons
        song={song}
        songStructure={songStructure}
        favorite={favorite}
        toggleFavorite={toggleFavorite}
        addToSetlist={addToSetlist}
        songKey={songKey}
        capo={capo}
        chordCharts={chordCharts}
      />
      <dl>
        <dt>{t('Title')}:</dt>
        <dd>{song.title}</dd>
        <dt>{t('Writers')}:</dt>
        <dd>{song.writer}</dd>
        <dt>{t('Artists')}:</dt>
        <dd>{artists(song)}</dd>
        <dt>{t('Album')}:</dt>
        <dd>
          <a href={albumPath(song.album)}>{song.album.name}</a>
        </dd>
        <dt>{t('Year')}:</dt>
        <dd>{song.album.year}</dd>
        <dt>{t('CCLI#')}:</dt>
        <dd>{song.ccliNumber}</dd>
        {song.copyrights && (
          <div>
            <dt>{t('Copyrights')}:</dt>
            <dd>{song.copyrights}</dd>
          </div>
        )}
      </dl>

      <div className={styles.streamingServices}>
        <a href={song.appleMusicUrl} target="_blank" rel="noreferrer">
          <FontAwesomeIcon icon={faApple} />
        </a>
        <a href={song.spotifyUrl} target="_blank" rel="noreferrer">
          <FontAwesomeIcon icon={faSpotify} />
        </a>
      </div>
    </div>

    <div className={`${styles.block} ${styles.songThemes}`}>
      {song.themes.length > 0 ? (
        <div className={styles.themesContainer}>
          <h3>{t('Themes')}</h3>

          <div className={styles.themes}>
            {song.themes.map((theme) => (
              <a
                key={theme.id}
                className={styles.theme}
                href={songsPath({ themeIds: [theme.id] })}
              >
                {theme.name}
              </a>
            ))}
          </div>
        </div>
      ) : (
        <></>
      )}

      {song.genres.length > 0 ? (
        <div className={styles.genresContainer}>
          <h3>{t('Genres')}</h3>

          <div className={styles.themes}>
            {song.genres.map((genre) => (
              <a
                key={genre.id}
                className={styles.theme}
                href={songsPath({ genreIds: [genre.id] })}
              >
                {genre.name}
              </a>
            ))}
          </div>
        </div>
      ) : (
        <></>
      )}

      <dl>
        <dt>{t('VIBE')}</dt>
        <dd>
          <a href={songsPath({ vibe: song.vibe.toLowerCase() })}>{song.vibe}</a>
        </dd>
        <dt>{t('DIFFICULTY')}</dt>
        <dd>
          <a
            href={songsPath({
              difficulties: [song.difficulty.toLowerCase()]
            })}
          >
            {song.difficulty}
          </a>
        </dd>
        <dt>{t('VOICE')}</dt>
        <dd>
          <a href={songsPath({ voice: song.voice.id })}>{song.voice.name}</a>
        </dd>
      </dl>
    </div>

    <SongCreditsStoreLinks song={song} />
  </aside>
)

const songKeyOption = (songKey: string): DropdownOption => {
  return keyOfOptions.find((option) => option.value === songKey)
}

export const SongPageContext = createContext(null)

const SongPage: NextPage<{
  ssrSong: Song
  ssrSongWithVideo
  ssrLyricsCharts: ChordSection[]
  ssrChordCharts: ChordSection[]
}> = ({ ssrSong, ssrSongWithVideo, ssrLyricsCharts, ssrChordCharts }) => {
  const router = useRouter()

  const getSong = (): Song => {
    return userSong || ssrSong
  }

  const getSongWithVideo = () => {
    return userSongWithVideo || ssrSongWithVideo
  }

  const getLyricsCharts = () => {
    return userLyricsCharts || ssrLyricsCharts
  }

  const getChordCharts = () => {
    return userChordCharts || ssrChordCharts
  }

  const [playingVideoInstrument, setPlayingVideoInstrument] = useState(null)
  const [songStructure, setSongStructure] = useState<ChordItem[]>([])
  const [chordItems, setChordItems] = useState<ChordItem[]>([])
  const [chordChanges, setChordChanges] = useState<Change[]>([])
  const [lyricsChanges, setLyricsChanges] = useState<Change[]>([])
  const [songKey, setSongKey] = useState(songKeyOption(ssrSong?.key))
  const [capo, setCapo] = useState(capoOptions[0])
  const { currentUser, currentUserFavorites, setCurrentUser } = useAuth()

  const [index, setIndex] = useState(2) // lyrics tab initially for seo
  const [userSong, setUserSong] = useState<Song>(null)
  const [userSongWithVideo, setUserSongWithVideo] = useState(null)
  const [userLyricsCharts, setUserLyricsCharts] = useState(null)
  const [userChordCharts, setUserChordCharts] = useState(null)

  const [expandedStatus, setExpandedStatus] = useState(true)

  const { id } = router.query

  const songId = parseInt(id as string)

  const song = getSong()
  const songWithVideo = getSongWithVideo()
  const lyricsCharts = getLyricsCharts()
  const chordCharts = getChordCharts()

  const [queryUserSong] = useLazyQuery(SONG_DETAILS_QUERY, {
    variables: { id: songId },
    onCompleted: (data) => {
      setUserSong(data.song as Song)
    },
    fetchPolicy: 'no-cache'
  })

  const [queryUserSongWithVideo] = useLazyQuery(SONG_VIDEO_QUERY, {
    variables: { id: songId },
    onCompleted: (data) => {
      setUserSongWithVideo(data.song)
    },
    fetchPolicy: 'no-cache'
  })

  const [queryUserLyricsCharts] = useLazyQuery(SONG_LYRICS_QUERY, {
    variables: { songId: songId },
    onCompleted: (data) => {
      setUserLyricsCharts(data.lyricsCharts)
    },
    fetchPolicy: 'no-cache'
  })

  const [queryUserChordCharts] = useLazyQuery(SONG_CHORDS_QUERY, {
    variables: {
      songId: songId,
      key: songKey?.value || song?.key,
      capo: Number(capo.value)
    },
    onCompleted: (data) => {
      setUserChordCharts(data.chordCharts)
    },
    fetchPolicy: 'no-cache'
  })

  useEffect(() => {
    if (id && currentUser) {
      queryUserSong()
      queryUserSongWithVideo()
      queryUserLyricsCharts()
      queryUserChordCharts()
    }
  }, [id, currentUser])

  useEffect(() => {
    if (userSong) {
      setChordChanges(userSong.userSetup.charts)
      setLyricsChanges(userSong.userSetup.lyrics)
      setSongKey(songKeyOption(userSong.userSetup.songKey || userSong.key))
      setCapo(
        capoOptions.find(
          (option) => option.value === (userSong.userSetup.capo || '0')
        )
      )
    }
  }, [userSong])

  const [persistChanges] = useMutation(SAVE_SONG_SETTINGS_MUTATION)

  const [toggleFavorite] = useMutation<
    { favoriteSongToggle: FavoriteSongToggleMutationPayload },
    { songId: number }
  >(FAVORITE_SONG_TOGGLE_MUTATION, {
    update: (_cache, result) => {
      setCurrentUser(result.data.favoriteSongToggle.user)
    }
  })

  useEffect(() => {
    if (userSong && currentUser) {
      persistChanges({
        variables: {
          songId: userSong.id,
          charts: chordChanges,
          lyrics: lyricsChanges,
          songKey: songKey?.value,
          capo: capo.value
        }
      })
    }
  }, [chordChanges, lyricsChanges, capo, songKey, userSong])

  useEffect(() => {
    if (userChordCharts) setIndex(0)
  }, [userChordCharts])

  const { openModal, closeModal } = useContext(ModalsContext)

  const { observe } = useInView({
    onEnter: ({ unobserve }) => {
      chordCharts && setIndex(0) // change lyrics to charts tab after user scrolls to panels
      unobserve()
    }
  })

  const openAddToSetlistModal = () => {
    openModal({
      name: 'AddToSetlist',
      props: {
        toggleModal: closeModal,
        song
      },
      modalTitle: t('Add Song To Setlist')
    })
  }

  const extractNames = (arrayOfObj) => {
    return (arrayOfObj || []).map((item) => {
      return { name: item.name }
    })
  }

  const handleSetSongStructure = (currentSongStructure) => {
    if (JSON.stringify(chordItems) !== JSON.stringify(currentSongStructure)) {
      setSongStructure(extractNames(currentSongStructure))
      setChordItems(currentSongStructure)
    }
  }

  const cleanDevotional = () => {
    return { __html: song.devotional }
  }

  if (!song) return <></> // needed for successul build as song can be null on ssr

  if (isNaN(Number(id)) && id !== song.slug) throw new Error('Wrong song path')

  return (
    <SongPageContext.Provider
      value={{
        playingVideoInstrument,
        setPlayingVideoInstrument,
        songWithVideo,
        lyricsCharts,
        persistChanges
      }}
    >
      <Layout
        pageTitle={`${song.title} - ${joinedArtistsStr(
          song.artists.slice(0, 1)
        )}`}
        keywords={song.seoKeywords}
        pageDescription={song.seoDescription}
        thumbnail={song.album.cover.medium}
        ogImage={song.album.cover.medium}
      >
        <div className={styles.songPage}>
          <div className={styles.instrumentsPlaceholder}>
            <SongInstruments song={song} />
          </div>
          <div className={styles.tabletActionButtons}>
            <SingleSongActionButtons
              song={song}
              songStructure={songStructure}
              songKey={songKey?.value}
              favorite={currentUserFavorites.has(song.id)}
              toggleFavorite={() =>
                toggleFavorite({ variables: { songId: song.id } })
              }
              addToSetlist={openAddToSetlistModal}
              capo={capo.value}
              chordCharts={chordItems}
            />
          </div>
          <div
            className={classnames(styles.songContainer, {
              [styles.expandSongContainer]: !expandedStatus
            })}
          >
            {expandedStatus && (
              <SongCredits
                song={song}
                songStructure={songStructure}
                songKey={songKey?.value}
                favorite={currentUserFavorites.has(song.id)}
                toggleFavorite={() =>
                  toggleFavorite({ variables: { songId: song.id } })
                }
                addToSetlist={openAddToSetlistModal}
                capo={capo.value}
                chordCharts={chordItems}
                copyrights={song.copyrights}
              />
            )}
            <main>
              <div
                className={styles.expandButton}
                onClick={() => setExpandedStatus(!expandedStatus)}
              />
              <Tabs state={[index, setIndex]}>
                <div className={styles.tabs}>
                  <Tab>{t('Charts')}</Tab>
                  <Tab>{t('Tabs')}</Tab>
                  <Tab>{t('Lyrics')}</Tab>
                </div>

                <div className={styles.panel} ref={observe}>
                  <Panel>
                    <div className={`${styles.panelContainer}`}>
                      <ChordChartsTab
                        song={song}
                        changes={chordChanges}
                        setChanges={setChordChanges}
                        setSongStructure={handleSetSongStructure}
                        songKey={songKey || songKeyOption(song?.key)}
                        setSongKey={setSongKey}
                        capo={capo}
                        setCapo={setCapo}
                        chordCharts={chordCharts || []}
                      />
                    </div>
                  </Panel>
                  <Panel>
                    <div className={`${styles.panelContainer}`}>
                      <TabInfosTab song={song} />
                    </div>
                  </Panel>
                  <Panel>
                    <div className={`${styles.panelContainer}`}>
                      <LyricsTab
                        song={song}
                        changes={lyricsChanges}
                        setChanges={setLyricsChanges}
                        setSongStructure={handleSetSongStructure}
                      />
                    </div>
                  </Panel>
                </div>
              </Tabs>
              {song.devotional && (
                <section
                  className={`content has-text-justified ${styles.songDescription}`}
                  id={'devotional'}
                >
                  <div className={styles.sectionTitle}>
                    <h2>{`${song.title} - In the Bible [Verses & Devotional]`}</h2>
                  </div>
                  <div className={styles.similarSongs}>
                    <div dangerouslySetInnerHTML={cleanDevotional()} />
                  </div>
                </section>
              )}
            </main>
          </div>
        </div>
      </Layout>
    </SongPageContext.Provider>
  )
}

export const getStaticPaths = staticPath({
  additionalPaths: async ({ client }) => {
    const { data } = await client.query({
      query: SONGS_LIST_QUERY,
      variables: filterStateToQueryVariables(initalFiltersState)
    })

    const paths = data.songs.map((song) => ({
      params: { id: song.id.toString() }
    }))

    return {
      paths,
      fallback: true
    }
  }
})

export const getStaticProps = staticProps({
  additionalProps: async ({ client, ctx }) => {
    const songId = parseInt(ctx.params.id)

    const [
      { data: songData },
      { data: songVideoData },
      { data: lyricsData },
      { data: chordsData }
    ] = await Promise.all([
      client.query({
        query: SONG_DETAILS_QUERY,
        variables: { id: songId }
      }),
      client.query({
        query: SONG_VIDEO_QUERY,
        variables: { id: songId }
      }),
      client.query({
        query: SONG_LYRICS_QUERY,
        variables: { songId }
      }),
      client.query({
        query: SONG_CHORDS_QUERY,
        variables: { songId }
      })
    ])

    const song = songData?.song || null
    return {
      ssrSong: song,
      ssrSongWithVideo: songVideoData?.song || null,
      ssrLyricsCharts: lyricsData?.lyricsCharts || null,
      ssrChordCharts: chordsData?.chordCharts || null
    }
  }
})

export default SongPage
